import RootAdmin from "../../../../components/layouts/RootAdmin";
import { Button, Card, CardBody, CardHeader, Chip, useDisclosure, Divider } from "@nextui-org/react";
import { LinkIcon } from "@heroicons/react/24/outline";
import FilterReactSelect from "../../../../components/elements/FilterReactSelect";
import { BASE_URL, getFetcher } from "../../../../services/api";
import useSWR from "swr";
import { useMemo } from "react";
import { formattedDate, hasValidRole } from "../../../../services/helpers";
import useAuth from "../../../../hooks/useAuth";
import RolesAccess from "../../../../enums/roles";
import toast from "react-hot-toast";
import ClientDataGrid from "../../../../components/elements/ClientDataGrid";
import { useSelectedCompany } from "../../../../hooks/useSelectedCompany";
import useListCompany from "../../../../hooks/useListCompany";
import { useGridApiRef } from '@mui/x-data-grid';
import ModalSKManual from "./ModalSKManual";

export default function AddSKPage() {
  const { user, roles } = useAuth();
  const isAdmin = hasValidRole(roles, [RolesAccess.SUPER_ADMIN]);
  const [selectedCompany, setSelectedCompany] = useSelectedCompany();
  const { dataCompany, isLoadingCompany } = useListCompany();
  const apiRef = useGridApiRef();

  const apiEndpoint = useMemo(() => {
    // 1. Prioritaskan pilihan dari dropdown (berlaku untuk Admin maupun User biasa)
    if (selectedCompany && selectedCompany.value) {
      return `/api/rekom/permohonan/search?companyId=${selectedCompany.value}&returnAll=true`;
    }
    
    // 2. Jika dropdown kosong (clear) dan user adalah Admin, tampilkan SEMUA data
    if (isAdmin) {
      return `/api/rekom/permohonan/search?returnAll=true`;
    }
    
    // 3. Jika dropdown kosong dan user BUKAN Admin, tampilkan data HANYA dari list perusahaannya
    const userCompanyIds = user.companies?.map((company) => company.companyId).join(",");
    return `/api/rekom/permohonan/search?companyId=${userCompanyIds}&returnAll=true`;
  }, [isAdmin, selectedCompany, user]);

  // 2. TAMBAHKAN 'mutate' UNTUK AUTO-REFRESH TABEL SETELAH SIMPAN DATA
  const { data, isLoading, mutate } = useSWR(apiEndpoint, getFetcher);

  // Modal state for SK selection
  const { isOpen: isSkModalOpen, onOpen: onSkModalOpen, onClose: onSkModalClose } = useDisclosure();

  // Open SK modal
  const handleOpenSkModal = () => {
    if (!selectedCompany) {
      toast.error("Silakan pilih perusahaan terlebih dahulu");
      return;
    }
    onSkModalOpen(); 
  }

  const columns = useMemo(
    () => [
      { field: "kodePermohonan", headerName: "Nomor Permohonan" },
      { field: "company", headerName: "Perusahaan", valueGetter: (value, row) => row?.company?.name || "-" },
      { field: "tipeSurat", headerName: "Tipe Permohonan" },
      { field: "nomorSuratRekomendasi", headerName: "Nomor Surat Rekomendasi" },
      { 
        field: "tanggalSuratRekomendasi", 
        headerName: "Tanggal Surat Rekomendasi", 
        valueGetter: (value, row) => 
          row?.tanggalSuratRekomendasi === row?.tanggalBerakhir 
            ? formattedDate(row?.tanggalBerlaku) 
            : formattedDate(row?.tanggalSuratRekomendasi) || "-" 
      },
      { field: "tanggalBerakhir", headerName: "Tanggal Berakhir", valueGetter: (value, row) => formattedDate(row?.tanggalBerakhir) ?? "-" },
      { field: "jumlahKendaraan", headerName: "Jumlah Kendaraan", valueGetter: (value, row) => row?.vehicles?.length || 0 },
      { field: "bahanB3", headerName: "Jumlah Bahan B3", valueGetter: (value, row) => row?.b3Substances?.length || 0 },
      {
        field: "status",
        headerName: "Status",
        renderCell: (params) => (
          <Chip size="sm" color="primary" variant="flat">
            {params?.row?.status}
          </Chip>
        ),
      },
      {
        field: "file_surat",
        headerName: "File SK",
        renderCell: (params) => {
          if (!params?.row?.file_surat) return "Belum Ada File SK";
          return (
            // Sudah memakai BASE_URL agar link tidak ganda
            <a href={`${BASE_URL}/${params.row.file_surat}`} target="_blank" rel="noopener noreferrer" className="text-primary-500 underline">
              Lihat File SK
            </a>
          );
        },
      },
    ],
    [],
  );

  return (
    <RootAdmin>
      <Card className="mt-3" radius="sm">
        <CardHeader>
          <p className="text-md">Daftar Permohonan Rekomendasi</p>
        </CardHeader>
        <Divider />
        <CardBody className="w-full h-[550px] p-5">
          <div className="w-full flex justify-between pb-3">
            <div className="w-1/4">
              <FilterReactSelect 
                defaultValue={selectedCompany} 
                isLoading={isLoadingCompany} 
                options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))} 
                setValue={setSelectedCompany} 
                placeholder="Pilih Perusahaan" 
              />
            </div>
            <div className="flex items-center">
              <Button 
                size="sm" 
                color="secondary" 
                startContent={<LinkIcon className="size-4 stroke-2" />} 
                onPress={handleOpenSkModal}
              >
                Tambah SK Manual
              </Button>
            </div>
          </div>
          <div className="w-full h-full">
            <ClientDataGrid
              apiRef={apiRef}
              rows={data?.applications}
              isLoading={isLoading}
              columns={columns}
            />
          </div>
        </CardBody>
      </Card>

      {/* 3. PANGGIL KOMPONEN MODAL DI SINI */}
      <ModalSKManual 
        isOpen={isSkModalOpen} 
        onClose={onSkModalClose} 
        selectedCompany={selectedCompany} 
        onSuccess={() => mutate()} 
      />

    </RootAdmin>
  );
}